/**
 * Helpers Zod reutilizables para validación de formularios — Deuda #031
 *
 * Documentos relacionados:
 *   - DECISIONES.md #031 (validación reforzada + patrón doble UI+BD)
 *   - docs/auditoria_validaciones.md (criterios y backlog de forms)
 *
 * Convenciones:
 *   - Mensajes 100% en español, sin tecnicismos.
 *   - Strings con `trim` aplicado por default (evita "  Nombre  " en BD).
 *   - Tope numérico de monto: 999_999_999.99 (numeric(14,2) técnico ~999B,
 *     dejamos margen contra typos).
 *   - Para campos críticos (montos, fechas relativas a otras), replicar
 *     defensa en BD (CHECK / trigger) — patrón #031.
 *
 * NO incluye validateCuit() (función pura) — ese está en `./validation.ts`.
 * Acá envolvemos esa función como helper Zod.
 */

import { z } from 'zod';
import { validateCuit } from './validation';
import { cuitDigits, todayLocal } from './format';

// ── Constantes públicas ──────────────────────────────────────────────────────

/** Tope técnico de seguridad para montos (numeric(14,2) Postgres). */
export const MONTO_MAX_DEFAULT = 999_999_999.99;

/** Mínimo de chars para password (admin policy ATSA). */
export const PASSWORD_MIN_LENGTH = 8;

// ── Texto ─────────────────────────────────────────────────────────────────────

interface TextoRequeridoOpts {
  label: string;
  min?: number;
  max?: number;
  /** Aplicar trim antes de validar (default true) */
  trim?: boolean;
}

/**
 * Texto requerido con trim, min/max length y mensajes en español.
 * Ejemplo: `zTextRequerido({ label: 'Razón social', max: 200 })`
 */
export function zTextRequerido({
  label,
  min = 1,
  max = 200,
  trim = true,
}: TextoRequeridoOpts) {
  return z
    .string({ required_error: `${label} es requerido` })
    .transform((v) => (trim ? v.trim() : v))
    .refine(
      (v) => v.length >= min,
      min === 1
        ? `${label} es requerido`
        : `${label} debe tener al menos ${min} caracteres`,
    )
    .refine(
      (v) => v.length <= max,
      `${label} no puede superar ${max} caracteres`,
    );
}

interface TextoOpcionalOpts {
  label: string;
  max?: number;
  trim?: boolean;
}

/**
 * Texto opcional con trim y max length. Acepta null/undefined/''.
 * Si viene un valor no-vacío, valida que no supere el max.
 */
export function zTextOpcional({ label, max = 200, trim = true }: TextoOpcionalOpts) {
  return z
    .string()
    .max(max, `${label} no puede superar ${max} caracteres`)
    .transform((v) => (trim ? v.trim() : v))
    .or(z.literal(''))
    .nullish();
}

// ── Email ─────────────────────────────────────────────────────────────────────

/** Email RFC válido + trim + lowercase. Mensajes en español. */
export function zEmail(label = 'Email') {
  return z
    .string({ required_error: `${label} es requerido` })
    .trim()
    .min(1, `${label} es requerido`)
    .email(`${label} no tiene un formato válido`)
    .max(120, `${label} no puede superar 120 caracteres`)
    .transform((v) => v.toLowerCase());
}

// ── Password ──────────────────────────────────────────────────────────────────

/**
 * Password fuerte: mínimo 8 chars + al menos 1 número.
 * NO exige símbolos (decisión UX: facilitar memorización para usuarios
 * adultos del sindicato).
 */
export function zPasswordFuerte(label = 'Contraseña') {
  return z
    .string({ required_error: `${label} es requerida` })
    .min(PASSWORD_MIN_LENGTH, `${label} debe tener al menos ${PASSWORD_MIN_LENGTH} caracteres`)
    .max(72, `${label} no puede superar 72 caracteres`) // bcrypt limit
    .refine((v) => /\d/.test(v), `${label} debe contener al menos un número`);
}

/** Password "simple" sólo para login (no validamos complejidad al ingresar). */
export function zPasswordLogin(label = 'Contraseña') {
  return z
    .string({ required_error: `${label} es requerida` })
    .min(1, `${label} es requerida`);
}

// ── CUIT / CUIL ───────────────────────────────────────────────────────────────

/**
 * CUIT/CUIL validado con algoritmo AFIP. Acepta formato con o sin guiones,
 * normaliza a dígitos puros.
 */
export function zCuit(label = 'CUIT') {
  return z
    .string({ required_error: `${label} es requerido` })
    .trim()
    .min(1, `${label} es requerido`)
    .transform((v) => cuitDigits(v))
    .refine(
      (v) => v.length === 11,
      `${label} debe tener 11 dígitos`,
    )
    .refine(
      (v) => validateCuit(v),
      `${label} inválido (dígito verificador AFIP no coincide)`,
    );
}

// ── Monto en pesos argentinos ─────────────────────────────────────────────────

interface MontoOpts {
  label?: string;
  /** Mínimo permitido (default 0.01 — debe ser positivo) */
  min?: number;
  /** Máximo permitido (default 999_999_999.99) */
  max?: number;
  /** Permitir cero (default false) */
  permitirCero?: boolean;
}

/**
 * Monto en ARS. Por default positivo, máximo `MONTO_MAX_DEFAULT`.
 * Acepta números o strings ("100.50") que se coercionan.
 */
export function zMontoARS({
  label = 'Monto',
  min = 0.01,
  max = MONTO_MAX_DEFAULT,
  permitirCero = false,
}: MontoOpts = {}) {
  const minEfectivo = permitirCero ? 0 : min;
  return z.coerce
    .number({
      required_error: `${label} es requerido`,
      invalid_type_error: `${label} debe ser numérico`,
    })
    .min(
      minEfectivo,
      permitirCero
        ? `${label} no puede ser negativo`
        : `${label} debe ser mayor a 0`,
    )
    .max(
      max,
      `${label} no puede superar $${max.toLocaleString('es-AR', { minimumFractionDigits: 2 })}`,
    );
}

// ── Porcentaje (0-100) ────────────────────────────────────────────────────────

/**
 * Porcentaje 0-100 con hasta 2 decimales. Default `max=100`, `min=0`.
 */
export function zPorcentaje({ label = 'Porcentaje', min = 0, max = 100 }: {
  label?: string;
  min?: number;
  max?: number;
} = {}) {
  return z.coerce
    .number({
      required_error: `${label} es requerido`,
      invalid_type_error: `${label} debe ser numérico`,
    })
    .min(min, `${label} no puede ser menor a ${min}`)
    .max(max, `${label} no puede ser mayor a ${max}`);
}

// ── Fechas ────────────────────────────────────────────────────────────────────

/** Devuelve la fecha de hoy en formato ISO yyyy-mm-dd (hora LOCAL, #491). */
function hoyIso(): string {
  return todayLocal();
}

/**
 * Fecha ISO `YYYY-MM-DD`, no puede ser futura.
 * Útil para fechas de nacimiento, alta, baja, pago, etc.
 */
export function zFechaNoFutura(label = 'Fecha') {
  return z
    .string({ required_error: `${label} es requerida` })
    .min(1, `${label} es requerida`)
    .refine(
      (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
      `${label} debe tener formato YYYY-MM-DD`,
    )
    .refine(
      (v) => v <= hoyIso(),
      `${label} no puede ser posterior a hoy`,
    );
}

/**
 * Fecha ISO `YYYY-MM-DD` en un rango [desde, hasta] (cualquiera opcional).
 * Útil para fechas con relación temporal a otras (ej: fecha_pago entre
 * fecha_emisión y hoy).
 */
export function zFechaEnRango({
  label = 'Fecha',
  desde,
  hasta,
  desdeLabel,
  hastaLabel,
}: {
  label?: string;
  desde?: string;
  hasta?: string;
  desdeLabel?: string;
  hastaLabel?: string;
}) {
  return z
    .string({ required_error: `${label} es requerida` })
    .min(1, `${label} es requerida`)
    .refine(
      (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
      `${label} debe tener formato YYYY-MM-DD`,
    )
    .refine(
      (v) => !desde || v >= desde,
      `${label} no puede ser anterior a ${desdeLabel ?? desde}`,
    )
    .refine(
      (v) => !hasta || v <= hasta,
      `${label} no puede ser posterior a ${hastaLabel ?? hasta}`,
    );
}

/** Fecha futura: para fechas de vencimiento. */
export function zFechaFutura(label = 'Fecha') {
  return z
    .string({ required_error: `${label} es requerida` })
    .min(1, `${label} es requerida`)
    .refine(
      (v) => /^\d{4}-\d{2}-\d{2}$/.test(v),
      `${label} debe tener formato YYYY-MM-DD`,
    )
    .refine(
      (v) => v >= hoyIso(),
      `${label} debe ser hoy o posterior`,
    );
}

// ── Período YYYY-MM ───────────────────────────────────────────────────────────

/** Período en formato `YYYY-MM` (ej: "2026-05"). */
export function zPeriodoYYYYMM(label = 'Período') {
  return z
    .string({ required_error: `${label} es requerido` })
    .trim()
    .regex(
      /^\d{4}-(0[1-9]|1[0-2])$/,
      `${label} debe tener formato YYYY-MM (ej: 2026-05)`,
    );
}

// ── Selectores (FK) ──────────────────────────────────────────────────────────

/**
 * UUID requerido para selectores FK (empresa, afiliado, período, etc.).
 * Mensaje genérico amigable.
 */
export function zSelectorRequerido(label: string) {
  return z
    .string({ required_error: `${label} es requerido` })
    .uuid(`${label} no es válido — seleccionalo de la lista`);
}

/** UUID opcional. */
export function zSelectorOpcional() {
  return z.string().uuid().nullish().or(z.literal(''));
}
